export type Constructor<T> = new (...a: any) => T;

export function mapValues<T, U>(
  obj: T,
  fn: (value: T[keyof T], key: keyof T, obj: T) => U,
): Record<string, U> {
  return Object.fromEntries(
    Object.entries(obj as Record<keyof T, T[keyof T]>).map(([key, value]) => [
      key,
      fn(value as T[keyof T], key as keyof T, obj),
    ]),
  );
}

type falsy = false | null | undefined;

export function flatMapEntries<T, U>(
  obj: T,
  fn: (key: keyof T, value: T[keyof T]) => [PropertyKey, U] | [PropertyKey, U][] | falsy,
): Record<PropertyKey, U> {
  return Object.fromEntries(
    Object.entries(obj as Record<keyof T, T[keyof T]>).flatMap(([key, value]) => {
      let v = fn(key as keyof T, value as T[keyof T]);
      if (!v) return [];
      if (Array.isArray(v[0])) return v as [PropertyKey, U][];
      return [v];
    }),
  );
}

// For a Registry<string, V1 | V2> return {...V1, ...V2}
export type MergeValues<
  T,
  U = T[keyof T],
  AllKeys extends PropertyKey = U extends any ? keyof U : never,
  CommonKeys extends PropertyKey = keyof U,
> = {
  [K in keyof ({ [K in CommonKeys]: U extends Record<K, infer V> ? V : never } & {
    [K in Exclude<AllKeys, CommonKeys>]?: U extends Record<K, infer V> ? V : never;
  })]: ({ [K in CommonKeys]: U extends Record<K, infer V> ? V : never } & {
    [K in Exclude<AllKeys, CommonKeys>]?: U extends Record<K, infer V> ? V : never;
  })[K];
};

type x = MergeValues<{
  A: {
    a: 1;
    b: 0;
  };
  B: {
    a: 2;
    c: 0;
  };
}>;
// should be { a: 1 | 2, b?: 0, c?: 0 }

export function sum(a: number, b: number): number {
  return a + b;
}

export function FakeImplements<Interface>() {
  return <C extends Constructor<any>>(cls: C) => {
    return cls as any as Pick<C, keyof C> &
      (new (...a: ConstructorParameters<C>) => InstanceType<C> & Interface);
  };
}

export function dedent(s: TemplateStringsArray | string): string {
  if (typeof s !== "string") {
    s = s.join("");
  }
  let lines = s.split("\n").filter(Boolean);
  while (lines[0]?.trim() === "") {
    lines.shift();
  }
  while (lines.at(-1)?.trim() === "") {
    lines.pop();
  }
  let minIndent = Math.min(...lines.map((line) => line.match(/^[^\S\n]+/)?.[0]?.length ?? 0));
  return lines.map((line) => line.slice(minIndent)).join("\n");
}
